import { marked } from "marked";

marked.setOptions({
  gfm: true,
  breaks: true,
});

const URL_REGEX = /https?:\/\/[^\s)]+/g;
// Identieke vorm als de server-side parser in db/mentions.ts — beide
// kanten moeten precies hetzelfde herkennen, anders highlight de UI iets
// wat de server niet als mention zag opslaan (of andersom).
const MENTION_REGEX = /(^|[^A-Za-z0-9_])@([A-Za-z0-9_]+)/g;

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Markeert `@username`, `@here` en `@channel` in een markdown-body met
 *  een placeholder die ná markdown-rendering wordt vervangen door een
 *  klikbare span. Doen we het andersom (HTML voor markdown), dan ziet
 *  marked de HTML als plain text en escapet hij 'm. `selfHighlight` zet
 *  een extra klasse zodat eigen-mentions opvallen.
 *
 *  Het placeholder-pad gebruikt een marker die markdown niet zelf gaat
 *  bewerken: een 0xE000-PUA-codepoint voorkomt collisies met emoji of
 *  bestaande tekst. */
const MARKER = "";

export function renderMarkdown(
  body: string,
  opts?: { highlightSelf?: string },
): string {
  // 1. Eerst de mentions vervangen door een placeholder die de markdown-
  //    parser ongemoeid laat. Whitespace vóór de @ houden we vast met $1.
  const tokens: Array<{ name: string }> = [];
  const pre = body.replace(MENTION_REGEX, (_full, lead: string, name: string) => {
    const i = tokens.length;
    tokens.push({ name });
    return `${lead}${MARKER}${i}${MARKER}`;
  });

  // 2. Markdown renderen, links + target=_blank zoals voorheen.
  let html = marked.parse(pre, { async: false }) as string;
  html = html.replace(/<a\s+href=/g, '<a target="_blank" rel="noopener noreferrer" href=');

  // 3. Placeholders vervangen door spans. self-mention krijgt een
  //    achtergrond-highlight zodat 'm in een drukke kanaalstream opvalt.
  const selfLc = opts?.highlightSelf?.toLowerCase();
  html = html.replace(
    new RegExp(`${MARKER}(\\d+)${MARKER}`, "g"),
    (_m, idx: string) => {
      const tok = tokens[Number(idx)];
      if (!tok) return "";
      const safe = escapeHtml(tok.name);
      const lc = tok.name.toLowerCase();
      const isSelf = selfLc !== undefined && lc === selfLc;
      const isBroadcast = lc === "here" || lc === "channel";
      const base = "rounded px-1 font-medium";
      const tone =
        isSelf
          ? "bg-purple-500/30 text-purple-100"
          : isBroadcast
          ? "bg-amber-500/15 text-amber-300"
          : "text-purple-300";
      return `<span class="${base} ${tone}">@${safe}</span>`;
    },
  );
  return html;
}

export function linkify(body: string): string {
  return escapeHtml(body).replace(
    URL_REGEX,
    (m) => `<a target="_blank" rel="noopener noreferrer" href="${m}">${m}</a>`,
  );
}
